import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const graphqlApiToken = '9658fbb2e193704a782a88018a2a7286';
const urlExistsCache = new Map();


/** @param {string} url */
async function checkUrlExists(url) {

    if (!url) return false;
    const cachedResult = urlExistsCache.get(url);
    if (cachedResult) return cachedResult;

    const request = (async () => {
        try {
            const response = await fetch(url, {
                method: 'HEAD',
                signal: AbortSignal.timeout(5000) // 5 second timeout
            });
            return response.ok; // Returns true for 200-299 status codes
        } catch (error) {
            return false;
        }
    })();

    urlExistsCache.set(url, request);

    try {
        return await request;
    } catch (error) {
        urlExistsCache.delete(url);
        return false;
    }
}
class CustomSlider extends HTMLElement {
    /** @type {boolean} */
    #isUpdating = false;
    /** @type {string | null} */
    #currentBreakpoint = null;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    #resizeTimeout = undefined;
    /** @type {string | null} */
    #lastResizeBreakpoint = null;

    constructor() {
        super();
        // Convert string attributes to booleans
        this.enableSliderMobile = this.getAttribute('data-enable-slider-mobile') === 'true';
        this.enableSliderDesktop = this.getAttribute('data-enable-slider-desktop') === 'true';
        this.slidesToShowDesktop = this.getAttribute('data-slides-to-show-desktop');
        this.slidesToShowMobile = this.getAttribute('data-slides-to-show-mobile');
        this.gap = this.getAttribute('data-gap');
        this.slider = null;
        this.abortController = new AbortController();
    }

    connectedCallback() {
        
        // Initialize last breakpoint for resize tracking
        this.#lastResizeBreakpoint = isMobileBreakpoint() ? 'mobile' : 'desktop';
        
        // Wait for next frame to ensure DOM is ready and container has width
        requestAnimationFrame(() => {
            // Initialize slider based on current breakpoint
            this.#updateSlider();

            // Listen to media query changes for responsive behavior
            mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);

        });
    }

    disconnectedCallback() {
        // Clear any pending resize timeout
        if (this.#resizeTimeout) {
            clearTimeout(this.#resizeTimeout);
            this.#resizeTimeout = undefined;
        }
        
        // Clean up event listeners
        this.abortController.abort();
        
        // Destroy slider if it exists
        this.#destroySlider();
    }

    /**
     * Handles media query change events to update slider state
     */
    #handleMediaQueryChange = () => {
        // Prevent recursive calls
        if (this.#isUpdating) return;
        // Update last resize breakpoint to match
        this.#lastResizeBreakpoint = isMobileBreakpoint() ? 'mobile' : 'desktop';
        this.#updateSlider();
    };

    /**
     * Checks if the slider should be enabled based on current breakpoint and settings
     * @returns {boolean} True if slider should be enabled
     */
    #shouldEnableSlider() {
        const isMobile = isMobileBreakpoint();
        const isDesktop = !isMobile;

        // If both are enabled, always enable
        if (this.enableSliderMobile && this.enableSliderDesktop) {
            return true;
        }

        // If only mobile is enabled, enable on mobile
        if (this.enableSliderMobile && !this.enableSliderDesktop) {
            return isMobile;
        }

        // If only desktop is enabled, enable on desktop
        if (this.enableSliderDesktop && !this.enableSliderMobile) {
            return isDesktop;
        }

        // If neither is enabled, don't enable
        return false;
    }

    /**
     * Updates the slider state based on current breakpoint
     */
    #updateSlider() {
        // Prevent recursive calls
        if (this.#isUpdating) return;
        
        this.#isUpdating = true;
        
        try {
            const shouldEnable = this.#shouldEnableSlider();
            const currentBreakpoint = isMobileBreakpoint() ? 'mobile' : 'desktop';
            const breakpointChanged = this.#currentBreakpoint !== currentBreakpoint;
            this.#currentBreakpoint = currentBreakpoint;

            // If slider should be enabled
            if (shouldEnable) {
                // If both mobile and desktop are enabled, use responsive config
                // and only initialize once
                if (this.enableSliderMobile && this.enableSliderDesktop) {
                    if (!this.slider) {
                        this.#initSlider(true); // Use responsive config
                    }
                    // Don't reinitialize if breakpoint changes - let responsive config handle it
                } else {
                    // Only one breakpoint is enabled, need to destroy/reinitialize on breakpoint change
                    if (!this.slider) {
                        this.#initSlider(false); // Don't use responsive config
                    } else if (breakpointChanged) {
                        // Breakpoint changed, need to update
                        this.#destroySlider();
                        this.#initSlider(false);
                    }
                }
            } else {
                // Slider should be disabled - always destroy if it exists
                // Check both the slider instance and DOM structure
                const hasTnsOuter = this.querySelector('.tns-outer') !== null;
                const hasSliderInstance = this.slider !== null;
                const hasTnsClasses = this.classList.contains('tns-slider');
                
                if (hasSliderInstance || hasTnsOuter || hasTnsClasses) {
                    this.#destroySlider();
                }
            }
        } finally {
            this.#isUpdating = false;
        }
    }

    /**
     * Initializes the Tiny Slider
     * @param {boolean} useResponsive - Whether to use responsive config (when both mobile and desktop are enabled)
     */
    #initSlider(useResponsive = false) {
        // Prevent multiple initializations
        if (this.slider) {
            return;
        }

        // Only initialize if tns is available (loaded from CDN)
        if (typeof tns === 'undefined') {
            console.warn('Tiny Slider (tns) is not loaded yet');
            return;
        }

        /** @type {Parameters<typeof tns>[0]} */
        const config = {
            container: this,
            slideBy: 1,
            gutter: this.gap ? parseInt(this.gap, 10) : 0,
            edgePadding: 0,
            controls: false,
            navPosition: 'bottom',
            nav: true,
            mouseDrag: true,
            loop: false,
        };

        // If both mobile and desktop are enabled, use responsive config
        // Base items should be mobile, then override at 750px for desktop
        if (useResponsive) {
            config.items = parseFloat(this.slidesToShowMobile || '1') || 1;
            config.responsive = {
                750: {
                    items: parseFloat(this.slidesToShowDesktop || '1') || 1,
                }
            };
        } else {
            // Only one breakpoint is enabled, use fixed items based on current breakpoint
            const isMobile = isMobileBreakpoint();
            const itemsToShow = isMobile ? this.slidesToShowMobile : this.slidesToShowDesktop;
            config.items = parseFloat(itemsToShow || '1') || 1;
        }

        this.slider = tns(config);
    }

    /**
     * Destroys the Tiny Slider instance
     */
    #destroySlider() {
        // Destroy the slider instance first if it exists
        // But only if the element is still in the DOM
        if (this.slider && this.isConnected) {
            try {
                if (typeof this.slider.destroy === 'function') {
                    // Check if we're still in the DOM before destroying
                    if (this.parentNode || document.body.contains(this)) {
                        this.slider.destroy();
                    }
                }
            } catch (error) {
                console.error('[CustomSlider] Error destroying slider:', error);
            } finally {
                this.slider = null;
            }
        } else if (this.slider) {
            // Slider exists but element is disconnected - just clear the reference
            this.slider = null;
        }
        
        // Force a reflow to ensure DOM updates are applied
        void this.offsetHeight;
    }
}

customElements.define('custom-slider', CustomSlider);

class PDPAAI extends HTMLElement {
    constructor() {
        super();
        this.productName = this.getAttribute('data-product-name');
        this.productId = this.getAttribute('data-product-id');
        this.vendor = this.getAttribute('data-vendor');
        this.endpoint = Theme.routes.storefrontAPI;
        this.token = graphqlApiToken;
        this.data = [];
    }

    connectedCallback() {
        this.fetchProducts();
    }

    async fetchProducts() {
        try {
            const res = await fetch(this.endpoint, {
              method: 'POST',
              headers: {
                'X-Shopify-Storefront-Access-Token': this.token,
                'Content-Type': 'application/graphql',
            },
            body: `{ 
                products(first: 200, query: "title:${this.productName}* AND vendor:${this.vendor}") {
                  edges {
                    node {
                      id
                      availableForSale
                      title
                      handle
                      vendor
                      onlineStoreUrl
                      metafield(key: "colour_group", namespace: "custom") {
                        value
                      }
                    }
                  }
                }
              }`
            });
      
            const data = await res.json();
      
            if (res.ok) {
                this.data = data.data.products.edges;
                this.renderElements();
            }
        } catch (error) {
            console.log(error)
        }
    }

    async renderElements() {
        let count = 0;
        let aaiHtml = '';
        
        if(this.data.length > 0) {
            // Collect all potential swatches first
            /** @type {Array<Promise<{exists: boolean, swatchUrl: string, colour: string, colourBackup: string, onlineStoreUrl: string, isCurrentProduct: boolean}>>} */
            const swatchPromises = [];
            
            this.data.forEach(product => {
                const productTitle = product.node.title.split(' | ')[1];
                const colour = product.node.title.split(' | ')[2];
                if(!colour) return;
                // const colourSlug = colour.toLowerCase().replaceAll(' & ', '-').replaceAll('&', '-').replaceAll(' / ', '-').replaceAll('/', '-').replaceAll(' ', '-');
                let colourGroup = null;
                const colourGroupRaw = product.node.metafield?.value;
                // The value is in a text field, but looks like an array, so it will look like this: "[\"White\",\"Red\"]", but I just need to get the first value
                if (colourGroupRaw) {
                    if (colourGroupRaw[0] === '[') {
                        try {
                            const parsedColourGroup = JSON.parse(colourGroupRaw);
                            colourGroup = Array.isArray(parsedColourGroup) ? (parsedColourGroup[0] || null) : colourGroupRaw;
                        } catch {
                            colourGroup = colourGroupRaw;
                        }
                    } else {
                        colourGroup = colourGroupRaw;
                    }
                }
                const colourGroupSlug = colourGroup ? colourGroup.toLowerCase().replaceAll(' & ', '-').replaceAll('&', '-').replaceAll(' / ', '-').replaceAll('/', '-').replaceAll(' ', '-') : null;
                const colourBackup = colour.toLowerCase().split(' ')[0];
                const vendor = product.node.vendor;
                const onlineStoreUrl = product.node.onlineStoreUrl;
                const productId = product.node.id.replace('gid://shopify/Product/', '');
                const isCurrentProduct = this.productId === productId;
                
                if(productTitle === this.productName && vendor === this.vendor && onlineStoreUrl) {
                    // let swatchUrl = Theme.template.swatchUrl?.replace('example', colourSlug);
                    const colourGroupSlugUrl = colourGroupSlug
                    ? Theme.template.swatchUrl?.replace('example', colourGroupSlug)
                    : null;

                if (colourGroupSlugUrl) {
                    swatchPromises.push(
                        (async () => {
                            let exists = await checkUrlExists(colourGroupSlugUrl);

                            return {
                                exists,
                                swatchUrl: colourGroupSlugUrl,
                                colour,
                                colourBackup,
                                onlineStoreUrl,
                                isCurrentProduct
                            };
                        })()
                    );
                }
                }
            });
            
            // Wait for all URL checks to complete
            const swatchResults = await Promise.all(swatchPromises);
            
            // Always render the div, conditionally render img based on URL check
            swatchResults.forEach((result) => {
                count++;
                const imgTag = result.exists 
                    ? `<img src="${result.swatchUrl}" alt="${result.colour}">`
                    : `<span class="pdp-aai-product-colour-backup" style="background-color: ${result.colourBackup};"></span>`;
                aaiHtml += `
                    <a href="${result.onlineStoreUrl}" class="pdp-aai-product colour-swatch ${result.isCurrentProduct ? 'active' : ''}" title="${result.colour}">
                        ${imgTag}
                    </a>
                `;
            });
        }
        
        if(count > 1) {
            let innerHtml = `<h3 class="pdp-aai-title h6">${this.getAttribute('data-section-title')}</h3><div class="pdp-aai-products">`;
            innerHtml += aaiHtml;
            innerHtml += `</div>`;
            this.innerHTML = innerHtml;
        } else {
            this.remove()
        }
    }
}

customElements.define('pdp-aai', PDPAAI);

class ReadMore extends HTMLElement {
    /** @type {HTMLElement | null} */
    contentWrapper = null;
    /** @type {HTMLElement | null} */
    content = null;
    /** @type {HTMLButtonElement | null} */
    toggle = null;
    /** @type {boolean} */
    isExpanded = false;
    /** @type {((event: Event) => void) | null} */
    boundToggleHandler = null;
    /** @type {number} */
    collapsedLines = 3;
    /** @type {string} */
    readMoreText = 'Read more';
    /** @type {string} */
    readLessText = 'Read less';

    constructor() {
        super();
        const collapsedLinesAttr = this.getAttribute('data-collapsed-lines');
        const readMoreAttr = this.getAttribute('data-read-more-text');
        const readLessAttr = this.getAttribute('data-read-less-text');
        
        this.collapsedLines = collapsedLinesAttr ? parseInt(collapsedLinesAttr, 10) : 3;
        this.readMoreText = readMoreAttr || 'Read more';
        this.readLessText = readLessAttr || 'Read less';
        this.contentWrapper = this.querySelector('.read-more-content');
        this.content = this.contentWrapper?.querySelector('.text-block') || null;
        this.toggle = this.querySelector('.read-more-toggle');
    }

    connectedCallback() {
        this.contentWrapper = this.querySelector('.read-more-content');
        this.content = this.contentWrapper?.querySelector('.text-block') || null;
        this.toggle = this.querySelector('.read-more-toggle');
        if (!this.contentWrapper || !this.content) return;

        // Start in collapsed state by default
        this.contentWrapper.classList.add('collapsed');
        this.contentWrapper.classList.remove('expanded');
        
        // Use double requestAnimationFrame to ensure DOM and CSS are fully applied
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.checkIfTruncationNeeded();
            });
        });
        
        this.setupEventListeners();
    }

    disconnectedCallback() {
        // Clean up event listener when element is removed from DOM
        if (this.toggle && this.boundToggleHandler) {
            this.toggle.removeEventListener('click', this.boundToggleHandler);
            this.boundToggleHandler = null;
        }
    }

    checkIfTruncationNeeded() {
        if (!this.contentWrapper || !this.content) return;

        // First, measure the full content height (expanded state)
        this.contentWrapper.classList.remove('collapsed');
        this.contentWrapper.classList.add('expanded');
        
        // Force a reflow to ensure expanded state is applied
        void this.contentWrapper.offsetHeight;
        void this.content.offsetHeight;
        
        const fullHeight = this.content.scrollHeight;
        
        // Now measure the collapsed/visible height
        this.contentWrapper.classList.remove('expanded');
        this.contentWrapper.classList.add('collapsed');
        
        // Force a reflow to ensure collapsed state and line-clamp are applied
        void this.contentWrapper.offsetHeight;
        void this.content.offsetHeight;
        
        // With line-clamp, the clientHeight represents the visible clamped height
        // Compare the full scrollHeight (expanded) to the visible clientHeight (collapsed)
        const collapsedVisibleHeight = this.content.clientHeight;
        
        // Content needs truncation if the full height is greater than the collapsed visible height
        // Add a small tolerance (2px) to account for rounding differences
        const shouldTruncate = fullHeight > (collapsedVisibleHeight + 2);
        
        if (shouldTruncate && this.toggle) {
            // Keep collapsed state
            this.contentWrapper.classList.remove('expanded');
            this.contentWrapper.classList.add('collapsed');
            this.toggle.style.display = 'inline-block';
            this.toggle.textContent = this.readMoreText;
            this.toggle.setAttribute('aria-expanded', 'false');
            this.isExpanded = false;
        } else {
            // Content doesn't need truncation, show fully expanded
            this.contentWrapper.classList.remove('collapsed');
            this.contentWrapper.classList.add('expanded');
            if (this.toggle) {
                this.toggle.style.display = 'none';
            }
        }
    }

    setupEventListeners() {
        if (this.toggle) {
            // Remove existing event listener if it exists
            if (this.boundToggleHandler) {
                this.toggle.removeEventListener('click', this.boundToggleHandler);
            }
            // Create and store bound handler
            this.boundToggleHandler = this.toggleContent.bind(this);
            // Add event listener (handler is guaranteed to be non-null after assignment)
            if (this.boundToggleHandler) {
                this.toggle.addEventListener('click', this.boundToggleHandler);
            }
        }
    }

    toggleContent() {
        if (!this.contentWrapper || !this.toggle) return;
        
        this.isExpanded = !this.isExpanded;
        
        if (this.isExpanded) {
            this.contentWrapper.classList.remove('collapsed');
            this.contentWrapper.classList.add('expanded');
            this.toggle.textContent = this.readLessText;
            this.toggle.setAttribute('aria-expanded', 'true');
        } else {
            this.contentWrapper.classList.remove('expanded');
            this.contentWrapper.classList.add('collapsed');
            this.toggle.textContent = this.readMoreText;
            this.toggle.setAttribute('aria-expanded', 'false');
        }
    }
}

customElements.define('read-more', ReadMore);

// Boost Promo Tiles
document.addEventListener('boostLoaded', () => {
    const boostPromoTiles = document.querySelectorAll('.plp-promo');
    const boostProductGrid = document.querySelector('.boost-sd__product-list');
    if(boostPromoTiles.length > 0 && boostProductGrid) {
        boostPromoTiles.forEach(tile => {
            const clone = tile.cloneNode(true);
            boostProductGrid.appendChild(clone);
        });
    }
});

class PLPProductSwatches extends HTMLElement {
    constructor() {
        super();
        this.productName = this.getAttribute('data-product-name')?.split(' | ')[1];
        this.productId = this.getAttribute('data-product-id');
        this.vendor = this.getAttribute('data-vendor');
        this.endpoint = Theme.routes.storefrontAPI;
        this.token = graphqlApiToken;
        this.data = [];
    }

    connectedCallback() {
        this.fetchProducts();
    }

    async fetchProducts() {
        try {
            const res = await fetch(this.endpoint, {
              method: 'POST',
              headers: {
                'X-Shopify-Storefront-Access-Token': this.token,
                'Content-Type': 'application/graphql',
            },
            body: `{ 
                products(first: 200, query: "title:${this.productName}* AND vendor:${this.vendor}") {
                  edges {
                    node {
                      id
                      availableForSale
                      title
                      handle
                      vendor
                      onlineStoreUrl
                       metafield(key: "colour_group", namespace: "custom") {
                        value
                      }
                    }
                  }
                }
              }`
            });
      
            const data = await res.json();
      
            if (res.ok) {
                this.data = data.data.products.edges;
                this.renderElements();
            }
        } catch (error) {
            console.log(error)
        }
    }

    async renderElements() {
        let count = 0;
        let swatchesHtml = '';
        
        if(this.data.length > 0) {
            // Collect all potential swatches first
            /** @type {Array<Promise<{exists: boolean, swatchUrl: string, colour: string, colourBackup: string, onlineStoreUrl: string, isCurrentProduct: boolean}>>} */
            const swatchPromises = [];

            this.data.forEach(product => {
                const productTitle = product.node.title.split(' | ')[1];
                const colour = product.node.title.split(' | ')[2];
                if(!colour) return;
                // const colourSlug = colour.toLowerCase().replaceAll(' & ', '-').replaceAll('&', '-').replaceAll(' / ', '-').replaceAll('/', '-').replaceAll(' ', '-');
                let colourGroup = null;
                const colourGroupRaw = product.node.metafield?.value;
                // The value is in a text field, but looks like an array, so it will look like this: "[\"White\",\"Red\"]", but I just need to get the first value
                if (colourGroupRaw) {
                    if (colourGroupRaw[0] === '[') {
                        try {
                            const parsedColourGroup = JSON.parse(colourGroupRaw);
                            colourGroup = Array.isArray(parsedColourGroup) ? (parsedColourGroup[0] || null) : colourGroupRaw;
                        } catch {
                            colourGroup = colourGroupRaw;
                        }
                    } else {
                        colourGroup = colourGroupRaw;
                    }
                }
                const colourGroupSlug = colourGroup ? colourGroup.toLowerCase().replaceAll(' & ', '-').replaceAll('&', '-').replaceAll(' / ', '-').replaceAll('/', '-').replaceAll(' ', '-') : null;
                const colourBackup = colour.toLowerCase().split(' ')[0];
                const vendor = product.node.vendor;
                const onlineStoreUrl = product.node.onlineStoreUrl;
                const productId = product.node.id.replace('gid://shopify/Product/', '');
                const isCurrentProduct = this.productId === productId;
                
                if(productTitle === this.productName && vendor === this.vendor && onlineStoreUrl) {
                    // const colourSlugUrl = Theme.template.swatchUrl?.replace('example', colourSlug);
                    const colourGroupSlugUrl = colourGroupSlug
                        ? Theme.template.swatchUrl?.replace('example', colourGroupSlug)
                        : null;

                    if (colourGroupSlugUrl) {
                        swatchPromises.push(
                            (async () => {
                                let exists = await checkUrlExists(colourGroupSlugUrl);
                                return {
                                    exists,
                                    swatchUrl: colourGroupSlugUrl,
                                    colour,
                                    colourBackup,
                                    onlineStoreUrl,
                                    isCurrentProduct
                                };
                            })()
                        );
                    }
                }
            });
            
            // Wait for all URL checks to complete
            const swatchResults = await Promise.all(swatchPromises);
            
            // Always render the div, conditionally render img based on URL check
            swatchResults.forEach((result) => {
                count++;
                if(count <= 6) {
                    const imgTag = result.exists 
                    ? `<img src="${result.swatchUrl}" alt="${result.colour}">`
                    : `<span class="product-swatch-colour-backup" style="background-color: ${result.colourBackup};"></span>`;
                swatchesHtml += `
                    <span class="product-swatch colour-swatch" title="${result.colour}">
                        ${imgTag}
                    </span>
                `;
                }
            });
        }

        if(count > 1) {
            let innerHtml = `<div class="product-swatches">`;
            if(count > 6) {
                innerHtml += `<span class="product-swatches-extra"">+${count - 6}</span>`;
            }
            innerHtml += swatchesHtml;
            innerHtml += `</div>`;
            this.innerHTML = innerHtml;
        }
    }
}

customElements.define('plp-swatches', PLPProductSwatches);

const fetchCartData = async () => {
    const response = await fetch('/cart.js', {
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    const data = await response.json();
    return data;
}

const convertToCurrency = (amount) => {
    return Shopify?.currency?.rate ? Math.trunc(Shopify.currency.rate * Number(amount)) : Math.trunc(Number(amount))
}

// Currency decimal precision mapping
const CURRENCY_DECIMALS = {
    BHD: 3,
    BIF: 0,
    BYR: 0,
    CLF: 4,
    CLP: 0,
    DJF: 0,
    GNF: 0,
    IQD: 3,
    ISK: 0,
    JOD: 3,
    JPY: 0,
    KMF: 0,
    KRW: 0,
    KWD: 3,
    LYD: 3,
    MRO: 5,
    OMR: 3,
    PYG: 0,
    RWF: 0,
    TND: 3,
    UGX: 0,
    UYI: 0,
    UYW: 4,
    VND: 0,
    VUV: 0,
    XAF: 0,
    XAG: 0,
    XAU: 0,
    XBA: 0,
    XBB: 0,
    XBC: 0,
    XBD: 0,
    XDR: 0,
    XOF: 0,
    XPD: 0,
    XPF: 0,
    XPT: 0,
    XSU: 0,
    XTS: 0,
    XUA: 0,
};
const DEFAULT_CURRENCY_DECIMALS = 2;

// Format currency amount with appropriate decimal precision
const formatCurrencyAmount = (amount) => {
    const currencyCode = Shopify?.currency?.active?.toUpperCase() || '';
    const precision = (currencyCode && currencyCode in CURRENCY_DECIMALS) ? CURRENCY_DECIMALS[currencyCode] : DEFAULT_CURRENCY_DECIMALS;
    const formatted = amount.toFixed(precision);
    
    // Remove trailing zeros and decimal point if precision is 0 or if all decimals are zeros
    if (precision === 0) {
      return formatted.split('.')[0];
    }
    
    // Remove trailing zeros for currencies with decimals
    return formatted.replace(/\.?0+$/, '');
}

// Cart Threshold Bar functionality
const cartThresholdBar = async () => {
    const settings = {
      container: 'FSTContainer',
      progress_bar: 'FSTBar',
      message: 'FSTMessage'
    }
    const container = document.getElementById(settings.container);
    if(container) {
      const script = container.closest('.cart-drawer__threshold-bar')?.querySelector('script')?.textContent;
      if(!script) {
        return;
      }
      const jsonData = JSON.parse(script);
      const enabledTargets = jsonData.targets.filter(target => target.enabled);
      const allTargets = enabledTargets.map(obj => obj.value);
      const maxTarget = Math.max(...allTargets);
      const currencySymbolObj = Theme.storeCurrency?.find(currency => currency[Shopify.currency.active]) || {};
      const currencySymbol = currencySymbolObj[Shopify.currency.active] || '$';
  
      if (!maxTarget || maxTarget == 0) {
        return;
      }
  
      const calculateProgress = (cartJson) => {
         // Sort targets from lowest to highest and remove any empty targets
        const targets = enabledTargets.sort((a, b) => a.value - b.value).filter(target => target.value);
        const total = cartJson.total_price && cartJson.total_price > 0 ? cartJson.total_price : cartJson.final_price && cartJson.final_price > 0 ? cartJson.final_price : 0;
        const cart_total = total > 0 ? total / 100 : 0;
        const progress_value = (cart_total / convertToCurrency(maxTarget)) * 100;
        let message = '';
  
        const progressBar = document.getElementById(settings.progress_bar);
  
        if (progress_value >= 100){
          progressBar.setAttribute('value', 100);
          progressBar.parentElement.setAttribute('value', 100);
        }
        else {
          progressBar.setAttribute('value', progress_value ? progress_value : 0);
          progressBar.parentElement.setAttribute('value', progress_value ? progress_value : 0);
        }
  
        if (cart_total <= 0) {
          message = jsonData.messaging.default;
          if(message != '' && message != null) {
            document.getElementById(settings.message).classList.add('cart-drawer__threshold-bar__default-message');
          }
        }
        else {
          for (let target of targets) {
            const remaining = convertToCurrency(target.value) - cart_total;
            const promoDot = container.querySelector(`.promotion-button.promotion--${target.name}`);
            const remaining2decimal = formatCurrencyAmount(remaining);
  
            if (remaining <= 0) {
              message += `<span class="message--success">
                <svg xmlns="http://www.w3.org/2000/svg" width="9.969" height="7.541" viewBox="0 0 9.969 7.541">
                    <path d="M153.744,184.629a.663.663,0,0,1-.423.212.693.693,0,0,1-.428-.216l-2.693-2.693.856-.856,2.27,2.27,6-6.045.842.87Z" transform="translate(-150.2 -177.3)" fill="#514131"/>
                </svg>
              <span>${target.messaging.success}</span>
              </span>`;
              if (promoDot) promoDot.classList.add('active');
            }
            else {
              message += `<span class="message--remaining">${target.messaging.ongoing.replace('[remaining]', `${currencySymbol}${remaining2decimal ? remaining2decimal : target.value}`)}</span>`;
              if (promoDot) promoDot.classList.remove('active');
              break;
            }
          }
          document.getElementById(settings.message).classList.remove('cart-drawer__threshold-bar__default-message');
        }
        // Display message
        document.getElementById(settings.message).innerHTML = message;
      }
  
      const renderElements = () => {
        container.innerHTML = '';
        const labelHTML = document.createElement('label')
        labelHTML.classList.add('visually-hidden');
        labelHTML.setAttribute('for', settings.progress_bar)
        labelHTML.textContent = 'Promotion progress'
    
        const progressBarHTML = document.createElement('progress')
        progressBarHTML.id = settings.progress_bar
        progressBarHTML.setAttribute('max', 100)
        progressBarHTML.setAttribute('value', 0)
        progressBarHTML.dataset.target = convertToCurrency(maxTarget);
    
        const progressBarWrapper = document.createElement('div')
        progressBarWrapper.classList.add('fst-progress-bar-wrapper')
        progressBarWrapper.dataset.target = convertToCurrency(maxTarget);
        progressBarWrapper.appendChild(progressBarHTML)
    
        // Add all promos to progress bar wrapper
        for (const promo of enabledTargets) {
          if(promo.value > 0) {
            const elem = document.createElement('span');
            elem.dataset.target = convertToCurrency(promo.value);
            elem.dataset.currencySymbol = currencySymbol;
            elem.style.setProperty('--position', (promo.value/maxTarget) * 100);
            elem.style.setProperty('--value', `${convertToCurrency(promo.value)}`);
            elem.setAttribute('data-label', promo.label);
            elem.classList.add('promotion-button', `promotion--${promo.name}`);
            elem.setAttribute('aria-hidden', true);
            progressBarWrapper.appendChild(elem);
          }
        }
    
        container.appendChild(labelHTML);
        container.appendChild(progressBarWrapper);
    
        const messageHTML = document.createElement('div');
        messageHTML.id = settings.message;
        messageHTML.classList.add('fst-message');
    
        container.appendChild(messageHTML);
      }
  
      const init = async () => {
        renderElements();
        const cartData = await fetchCartData();
        if(cartData) {
          calculateProgress(cartData);
        }
      }
  
      const update = async () => {
        const cartData = await fetchCartData();
        if(cartData) {
          calculateProgress(cartData);
        }
      }
  
      document.addEventListener(ThemeEvents.cartUpdate, () => {
        const progressBar = document.getElementById(settings.progress_bar);
        if(progressBar) {
          update();
        } else {
          init();
        }
      });
  
      init();
    }
}

const shoppableLookbook = () => {
    const dots = document.querySelectorAll('.shoppable-lookbook-dot');
    dots.forEach(dot => {
        dot.addEventListener('click', () => {
            const target = dot.dataset.target;
            const parentContainer = dot.closest('.shoppable-lookbook-container');
            if(!parentContainer) return;
            const activeProduct = parentContainer.querySelector('.shoppable-lookbook-content-inner-products-item:not(.hidden)');
            if(activeProduct) {
                activeProduct.classList.add('hiding');
                setTimeout(() => {
                    activeProduct.classList.add('hidden');
                    activeProduct.classList.remove('hiding');
                }, 300);
            }
            const product = parentContainer.querySelector(`[data-id="${target}"]`);
            if(product) {
                product.classList.remove('hidden');
            }
        });
    });
}

document.addEventListener('DOMContentLoaded', () => {
    cartThresholdBar();
    shoppableLookbook();
})

const addToCart = async (variantId, qty, properties) => {
    const itemData = {
      'id': variantId,
      'quantity': qty
    };
    
    if(properties) {
      itemData.properties = properties;
    }
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(itemData)
    });
    
    if(response.ok) {
      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'upsell-form-component',
          itemCount: 1,
        })
      );
      return true;
    } else {
      const errorData = await response.json().catch(() => null);
      throw new Error('Failed to add to cart: ' + (errorData?.description || response.statusText));
    }
  }

document.addEventListener("change", (event) => { 
  const target = event.target;

  // Upsell variant change event
  if(target.name && target.name === 'upsell-variant') {
    const variantId = target.value;
    const parent = target.closest('.product-upsell-form');
    if(parent) {
      const atcButton = parent.querySelector('.product-upsell-form__button');
      if(atcButton) {
        atcButton.dataset.variantId = variantId;
      }
    }
  }
});

document.addEventListener("click", (event) => { 
  const target = event.target;

  // Upsell add to cart button click event
  if(target.classList.contains('product-upsell-form__button')) {
    target.disabled = true;
    const variantId = target.dataset.variantId;
    if(variantId) {
      addToCart(variantId, 1);
      target.disabled = false;
    }
  }
});
